import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Ban, MessageCircle, StickyNote, CalendarPlus, X, ChevronRight } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import crmService from '../../services/crmService';
import { useAppSettings } from '../../context/AppSettingsContext';
import { useT } from '../../context/LanguageContext';

/* ============================================================
 * CustomerProfilePage — the Customer 360.
 *
 * One guest, read whole: who they are, how they behave as a booker,
 * how they spend, what they come back for, where they go, and the
 * loyalty they have earned. Every figure is READ off orders and
 * reservations already recorded — nothing here is estimated, and a
 * block whose data does not exist yet says so instead of printing a
 * number (the loyalty card, until a scheme exists).
 * ============================================================ */

// The page's own palette — a guest profile is read at a glance across a
// desk, so the colours carry meaning: green kept, red missed, amber late,
// grey released, gold earned.
const GREEN = '#2f5d3a';
const GOLD = '#b8923d';
const RED = '#b23a2f';
const AMBER = '#b8862b';
const GREY = '#c9c4b8';

const BLOCK_STYLE = {
  honoured: { background: GREEN },
  no_show: { background: RED },
  paid_no_show: {
    backgroundImage: `repeating-linear-gradient(135deg, ${RED} 0 6px, #7a2119 6px 12px)`,
  },
  late_cancel: { background: AMBER },
  cancelled_ok: { background: GREY },
};

const DOW_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const digitsOf = (phone) => String(phone || '').replace(/\D/g, '');

export default function CustomerProfilePage() {
  const { t } = useT();
  const { customerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatCurrency, formatDate } = useAppSettings();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Header actions: the blacklist switch asks once inline; the note form is inline too.
  const [blAsk, setBlAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteKind, setNoteKind] = useState('NOTE');
  const [tab, setTab] = useState('orders');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    crmService.profileById(customerId)
      .then((p) => { if (alive) setProfile(p); })
      .catch((e) => { if (alive) setError(e?.message || t('crm.p.loadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const money = (n) => formatCurrency(Number(n) || 0);
  const day = (iso) => (iso ? formatDate(iso) : '—');
  const monthYear = (iso) => (iso
    ? new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '—');
  const dayMonth = (iso) => (iso
    ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—');
  const dateTime = (iso) => (iso
    ? `${formatDate(iso)}, ${new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : '—');
  const hourLabel = (h) => (h == null ? '' : new Date(2000, 0, 1, h, 0).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }));

  const c = profile?.customer;
  const chips = useMemo(() => {
    if (!profile) return [];
    const ch = profile.chips || {};
    const out = [];
    if (ch.tier) out.push({ tone: 'gold', text: t('crm.chip.tier', { tier: ch.tier }) });
    if (ch.watchlist_no_shows > 0) out.push({ tone: 'amber', text: t('crm.chip.watchlist', { n: ch.watchlist_no_shows }) });
    if (ch.favourite_category) out.push({ tone: 'plain', text: t('crm.chip.lover', { name: ch.favourite_category }) });
    if (ch.birthday) out.push({ tone: 'plain', text: t('crm.chip.birthday', { date: dayMonth(ch.birthday) }) });
    (ch.preferences || []).forEach((p) => out.push({ tone: 'plain', text: p.text, noteId: p.id }));
    if (ch.whatsapp) out.push({ tone: 'plain', text: t('crm.chip.whatsapp') });
    return out;
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyBlacklist = async () => {
    setBusy(true);
    try {
      const next = await crmService.setBlacklist(c.id, !c.is_blacklisted);
      setProfile(next);
      setBlAsk(false);
    } catch (e) {
      setError(e?.message || t('crm.blacklist.failed'));
    } finally { setBusy(false); }
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const next = await crmService.addNote(c.id, noteText.trim(), noteKind);
      setProfile(next);
      setNoteText('');
      setNoteOpen(false);
    } catch (e) {
      setError(e?.message || t('crm.p.noteFailed'));
    } finally { setBusy(false); }
  };

  const removeNote = async (id) => {
    setBusy(true);
    try {
      setProfile(await crmService.deleteNote(c.id, id));
    } catch (e) {
      setError(e?.message || t('crm.p.noteFailed'));
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="p-6"><Card className="p-10 text-center text-muted-foreground">{t('crm.p.loading')}</Card></div>;
  }
  if (!profile || !c) {
    return (
      <div className="p-6">
        <Card className="p-10 text-center text-red-600">{error || t('crm.p.loadFailed')}</Card>
      </div>
    );
  }

  const b = profile.behaviour;
  const r = profile.rhythm || {};
  const d = profile.dishes || { top: [] };
  const pl = profile.places || { visited: [], not_visited: [] };
  const loyalty = profile.loyalty;
  const recent = profile.recent || { orders: [], invoices: [], reservations: [] };
  const maxTimes = Math.max(1, ...(d.top || []).map((x) => x.times));
  const maxSpend = Math.max(1, ...(pl.visited || []).map((x) => x.spend));
  const maxMonth = Math.max(1, ...((r.monthly || []).map((m) => m.visits)));
  const from = searchParams.get('from');

  return (
    <div className="p-6 w-full max-w-[1320px]">
      {/* Breadcrumb */}
      <nav className="mb-4 text-sm text-muted-foreground flex items-center gap-1.5">
        <Link to="/customers" className="hover:text-foreground">{t('crm.p.crumbCustomers')}</Link>
        <span>/</span>
        <Link to="/customers" className="hover:text-foreground">{t('crm.p.crumbAll')}</Link>
        <span>/</span>
        <span className="text-foreground">{c.full_name}</span>
        {from && (
          <button type="button" onClick={() => navigate(-1)}
            className="ml-auto inline-flex items-center gap-1 hover:text-foreground">
            <ChevronRight size={14} className="rotate-180" /> {t('crm.p.back')}
          </button>
        )}
      </nav>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      {/* ---- Header --------------------------------------------------- */}
      <div className="flex items-start gap-5">
        <div className="h-20 w-20 shrink-0 rounded-full flex items-center justify-center text-2xl font-semibold text-white"
          style={{ background: GREEN }}>
          {profile.identity?.initials || '?'}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-[34px] font-semibold leading-none tracking-tight text-foreground">
            {c.full_name}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Phone size={14} /> {c.phone}</span>
            {!c.email_is_placeholder && (
              <span className="inline-flex items-center gap-1.5"><Mail size={14} /> {c.email}</span>
            )}
            {profile.identity?.city && (
              <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {profile.identity.city}</span>
            )}
            <span>{t('crm.p.memberSince', { when: monthYear(profile.identity?.member_since) })}</span>
            <span>{t('crm.p.guestId', { id: profile.identity?.guest_id })}</span>
          </div>
          {/* Chips: who this guest is, in a line */}
          <div className="mt-3 flex flex-wrap gap-2">
            {c.is_blacklisted && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-3 py-1 text-xs font-medium text-white"
                title={c.blacklist_reason || ''}>
                <Ban size={12} /> {t('crm.blacklist.badge')}
              </span>
            )}
            {chips.map((chip, i) => (
              <span key={i}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${
                  chip.tone === 'gold' ? 'border-[#e2cf9b] bg-[#f3e8c9] text-[#6b5320]'
                    : chip.tone === 'amber' ? 'border-[#e8d3a3] bg-[#f6ecd2] text-[#7a5a12]'
                      : 'border-border bg-background text-foreground'}`}>
                {chip.text}
                {chip.noteId && (
                  <button type="button" disabled={busy} onClick={() => removeNote(chip.noteId)}
                    className="ml-0.5 text-muted-foreground hover:text-red-600" title={t('crm.p.removePref')}>
                    <X size={11} />
                  </button>
                )}
              </span>
            ))}
            {chips.length === 0 && !c.is_blacklisted && (
              <span className="text-xs text-muted-foreground">{t('crm.p.noChips')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button type="button" className="text-white hover:opacity-90" style={{ background: GREEN }}
          onClick={() => navigate(`/reservations?phone=${encodeURIComponent(c.phone)}&name=${encodeURIComponent(c.full_name)}`)}>
          <CalendarPlus size={15} className="mr-1.5" /> {t('crm.p.newReservation')}
        </Button>
        <Button type="button" variant="outline"
          onClick={() => window.open(`https://wa.me/${digitsOf(c.phone)}`, '_blank', 'noopener')}>
          <MessageCircle size={15} className="mr-1.5" /> {t('crm.p.sendWhatsapp')}
        </Button>
        <Button type="button" variant="outline" onClick={() => setNoteOpen((v) => !v)}>
          <StickyNote size={15} className="mr-1.5" /> {t('crm.p.addNote')}
        </Button>
        {!blAsk ? (
          <Button type="button" variant="outline" disabled={busy} onClick={() => setBlAsk(true)}
            className={c.is_blacklisted ? 'text-emerald-700' : 'text-red-700'}>
            {c.is_blacklisted ? t('crm.blacklist.lift') : t('crm.p.blacklist')}
          </Button>
        ) : (
          <span className="inline-flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('crm.blacklist.sure')}{c.is_blacklisted ? ` ${t('crm.blacklist.liftHint')}` : ''}
            </span>
            <Button type="button" size="sm" disabled={busy} onClick={applyBlacklist}
              className="bg-red-600 text-white hover:bg-red-700">{t('crm.blacklist.yes')}</Button>
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setBlAsk(false)}>
              {t('crm.blacklist.no')}
            </Button>
          </span>
        )}
      </div>

      {/* Note form + diary */}
      {noteOpen && (
        <Card className="mt-3 p-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[16rem]">
              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('crm.p.noteLabel')}</label>
              <input value={noteText} maxLength={300} onChange={(e) => setNoteText(e.target.value)}
                placeholder={t('crm.p.notePlaceholder')}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30" />
            </div>
            <div className="inline-flex rounded-md border border-border bg-background p-0.5">
              {[['NOTE', t('crm.p.kindNote')], ['PREFERENCE', t('crm.p.kindPref')]].map(([k, label]) => (
                <button key={k} type="button" aria-pressed={noteKind === k} onClick={() => setNoteKind(k)}
                  className={`rounded px-2.5 py-1 text-xs font-semibold ${noteKind === k
                    ? 'text-white' : 'text-muted-foreground hover:bg-muted'}`}
                  style={noteKind === k ? { background: GREEN } : undefined}>
                  {label}
                </button>
              ))}
            </div>
            <Button type="button" disabled={busy || !noteText.trim()} onClick={saveNote}
              className="text-white" style={{ background: GREEN }}>{t('crm.p.saveNote')}</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t('crm.p.kindHint')}</p>
        </Card>
      )}
      {(profile.notes || []).filter((n) => n.kind === 'NOTE').length > 0 && (
        <Card className="mt-3 p-4">
          <div className="text-sm font-semibold text-foreground mb-2">{t('crm.p.notesTitle')}</div>
          <ul className="space-y-1.5">
            {profile.notes.filter((n) => n.kind === 'NOTE').map((n) => (
              <li key={n.id} className="flex items-start gap-2 text-sm">
                <span className="text-xs text-muted-foreground whitespace-nowrap mt-0.5">{day(n.created_at)}</span>
                <span className="flex-1 text-foreground">{n.text}
                  {n.author ? <span className="text-muted-foreground"> — {n.author}</span> : null}
                </span>
                <button type="button" disabled={busy} onClick={() => removeNote(n.id)}
                  className="text-muted-foreground hover:text-red-600" title={t('crm.p.removeNote')}><X size={13} /></button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <hr className="my-6 border-border" />

      {/* ---- Booking behaviour ---------------------------------------- */}
      <section>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{t('crm.p.behaviourTitle')}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {b && b.total > 0
                ? t('crm.p.behaviourSub', { n: b.total, locations: b.locations })
                : t('crm.p.behaviourNone')}
            </p>
          </div>
          {b && b.reliability_pct != null && (
            <div className="text-sm text-muted-foreground">
              <span className="text-2xl font-semibold text-foreground mr-1.5">{b.reliability_pct}%</span>
              {t('crm.p.reliability')}
              {b.advice === 'ADVANCE' ? ` · ${t('crm.p.adviceAdvance')}` : b.advice === 'NONE' ? ` · ${t('crm.p.adviceNone')}` : ''}
            </div>
          )}
        </div>

        {b && b.total > 0 && (
          <>
            <div className="mt-4 flex flex-wrap gap-1">
              {b.blocks.map((blk) => (
                <div key={blk.id} className="h-11 w-12 rounded-[3px]" style={BLOCK_STYLE[blk.kind]}
                  title={`${t(`crm.p.kind.${blk.kind}`)} · ${day(blk.date)}${blk.location ? ` · ${blk.location}` : ''}`} />
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
              {[
                ['honoured', GREEN, t('crm.p.card.honoured'), t('crm.p.card.honouredSub')],
                ['no_show', RED, t('crm.p.card.noShow'),
                  b.last_no_show ? t('crm.p.card.noShowSub', { date: day(b.last_no_show.date), location: b.last_no_show.location || '' }) : t('crm.p.card.none')],
                ['late_cancel', AMBER, t('crm.p.card.lateCancel'),
                  b.cancel_cutoff_minutes ? t('crm.p.card.lateCancelSub', { hours: Math.round(b.cancel_cutoff_minutes / 60 * 10) / 10 }) : t('crm.p.card.lateCancelSubPlain')],
                ['paid_no_show', RED, t('crm.p.card.paidNoShow'),
                  b.counts.paid_no_show ? t('crm.p.card.paidNoShowSub', { amount: money(b.forfeited), date: day(b.last_paid_no_show) }) : t('crm.p.card.none')],
                ['cancelled_ok', GREY, t('crm.p.card.cancelledOk'),
                  b.refunded_any ? t('crm.p.card.cancelledOkSub') : t('crm.p.card.cancelledOkSubNone')],
              ].map(([key, colour, label, sub]) => (
                <Card key={key} className="p-4 rounded-lg border-l-4" style={{ borderLeftColor: colour }}>
                  <div className="text-3xl font-semibold text-foreground">{b.counts[key] || 0}</div>
                  <div className="mt-1 text-sm text-foreground">{label}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
                </Card>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ---- Spend · Dishes · Locations -------------------------------- */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Spend and rhythm */}
        <Card className="p-5">
          <div className="flex items-baseline justify-between">
            <h3 className="text-lg font-semibold text-foreground">{t('crm.p.spendTitle')}</h3>
            <span className="text-xs text-muted-foreground">{t('crm.p.visitsN', { n: r.visits || 0 })}</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-[28px] font-semibold leading-none text-foreground">{money(r.avg_bill)}</div>
              <div className="mt-1.5 text-xs text-muted-foreground">{t('crm.p.avgBill')}</div>
            </div>
            <div>
              <div className="text-[28px] font-semibold leading-none text-foreground">
                {r.every_days ? t('crm.p.everyDays', { n: r.every_days }) : '—'}
              </div>
              <div className="mt-1.5 text-xs text-muted-foreground">
                {t('crm.p.visitFrequency')}{r.per_month ? ` · ${t('crm.p.perMonth', { n: r.per_month })}` : ''}
              </div>
            </div>
          </div>
          {/* Visits per month, oldest on the left */}
          <div className="mt-4 flex h-14 items-end gap-1.5" aria-hidden="true">
            {(r.monthly || []).map((m) => (
              <div key={`${m.year}-${m.month}`} className="flex-1 rounded-sm"
                title={`${m.month}/${m.year}: ${m.visits}`}
                style={{ height: `${Math.max(4, (m.visits / maxMonth) * 100)}%`, background: GOLD, opacity: m.visits ? 1 : 0.35 }} />
            ))}
          </div>
          <dl className="mt-4 space-y-2 text-sm">
            {[
              [t('crm.p.lifetimeSpend'), money(r.lifetime_spend)],
              [t('crm.p.highestBill'), r.highest_bill != null ? `${money(r.highest_bill)}${r.highest_bill_label ? ` · ${r.highest_bill_label}` : ''}` : '—'],
              [t('crm.p.usualParty'), r.party_from ? (r.party_from === r.party_to ? String(r.party_from) : t('crm.p.partyRange', { a: r.party_from, b: r.party_to })) : '—'],
              [t('crm.p.usualSlot'), r.usual_dow != null
                ? `${t(`crm.p.dow.${DOW_KEYS[r.usual_dow]}`)} ${t(`crm.p.meal.${r.usual_meal}`)}, ${hourLabel(r.usual_hour)}` : '—'],
              [t('crm.p.lastVisit'), day(r.last_visit)],
              [t('crm.p.nextReservation'), r.next_reservation ? dateTime(r.next_reservation.at) : t('crm.p.noneBooked')],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-right font-medium text-foreground">{v}</dd>
              </div>
            ))}
          </dl>
        </Card>

        {/* Repeat dishes */}
        <Card className="p-5">
          <div className="flex items-baseline justify-between">
            <h3 className="text-lg font-semibold text-foreground">{t('crm.p.dishesTitle')}</h3>
            <span className="text-xs text-muted-foreground">{t('crm.p.timesOrdered')}</span>
          </div>
          {(d.top || []).length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">{t('crm.p.noDishes')}</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {d.top.map((x) => (
                <li key={x.item}>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground">{x.item}</span>
                    <span className="font-medium text-foreground">{x.times}×</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-muted">
                    <div className="h-1.5 rounded-full" style={{ width: `${(x.times / maxTimes) * 100}%`, background: GREEN }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          {d.upsell && (
            <p className="mt-4 text-xs text-muted-foreground">
              {t('crm.p.upsell', { item: d.upsell.item, with: d.upsell.with, pct: d.upsell.pct })}
            </p>
          )}
        </Card>

        {/* Locations visited */}
        <Card className="p-5">
          <div className="flex items-baseline justify-between">
            <h3 className="text-lg font-semibold text-foreground">{t('crm.p.placesTitle')}</h3>
            <span className="text-xs text-muted-foreground">{t('crm.p.visitsSpend')}</span>
          </div>
          {(pl.visited || []).length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">{t('crm.p.noPlaces')}</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {pl.visited.map((x) => (
                <li key={x.location_id}>
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-foreground truncate">{x.name}</span>
                      {x.brand && (
                        <span className="rounded-full bg-[#e6efe8] px-2 py-0.5 text-[11px] text-[#2f5d3a] whitespace-nowrap">{x.brand}</span>
                      )}
                    </span>
                    <span className="font-medium text-foreground whitespace-nowrap">{x.visits} · {money(x.spend)}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-muted">
                    <div className="h-1.5 rounded-full" style={{ width: `${(x.spend / maxSpend) * 100}%`, background: GREEN }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          {((pl.not_visited || []).length > 0 || pl.home) && (
            <p className="mt-4 text-xs text-muted-foreground">
              {(pl.not_visited || []).length > 0 ? `${t('crm.p.notVisited', { names: pl.not_visited.join(', ') })} ` : ''}
              {pl.home ? t('crm.p.homeBranch', { name: pl.home }) : ''}
            </p>
          )}
        </Card>
      </div>

      {/* ---- Loyalty rewards ------------------------------------------ */}
      <Card className="mt-5 p-5">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_1fr_1.2fr]">
          <div>
            <h3 className="text-lg font-semibold text-foreground">{t('crm.p.loyaltyTitle')}</h3>
            <div className="mt-3 flex items-center gap-4">
              <div className="h-14 w-14 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                style={{ background: loyalty ? GOLD : GREY }}>
                {loyalty ? loyalty.tier : '—'}
              </div>
              <div>
                <div className="text-[28px] font-semibold leading-none text-foreground">
                  {t('crm.p.points', { n: loyalty ? loyalty.points.toLocaleString() : 0 })}
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">
                  {loyalty
                    ? t('crm.p.redeemable', { amount: money(loyalty.redeemable), tier: loyalty.tier, since: monthYear(loyalty.tier_since) })
                    : t('crm.p.noLoyalty')}
                </div>
              </div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-muted">
              <div className="h-2 rounded-full" style={{ width: `${loyalty ? loyalty.progress_pct : 0}%`, background: GOLD }} />
            </div>
            <div className="mt-1.5 flex justify-between text-xs text-muted-foreground">
              <span>{loyalty ? t('crm.p.toNextTier', { n: loyalty.to_next.toLocaleString(), tier: loyalty.next_tier }) : t('crm.p.noTier')}</span>
              <span>{loyalty ? t('crm.p.tierResets', { date: day(loyalty.resets_at) }) : ''}</span>
            </div>
          </div>
          <dl className="space-y-2 text-sm self-center">
            {[
              [t('crm.p.earnedLifetime'), loyalty ? loyalty.earned.toLocaleString() : '—'],
              [t('crm.p.redeemed'), loyalty ? loyalty.redeemed.toLocaleString() : '—'],
              [loyalty?.expiring_at ? t('crm.p.expiring', { date: dayMonth(loyalty.expiring_at) }) : t('crm.p.expiringPlain'), loyalty ? loyalty.expiring.toLocaleString() : '—'],
              [t('crm.p.referrals'), loyalty ? t('crm.p.guestsN', { n: loyalty.referrals }) : '—'],
              [t('crm.p.memberSaved'), loyalty ? money(loyalty.member_saved) : '—'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="font-medium text-foreground">{v}</dd>
              </div>
            ))}
          </dl>
          <div>
            <div className="text-sm text-muted-foreground">{t('crm.p.vouchersTitle')}</div>
            {loyalty && (loyalty.vouchers || []).length > 0 ? (
              <ul className="mt-2 space-y-2">
                {loyalty.vouchers.map((v) => (
                  <li key={v.id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
                    <span className="text-foreground">{v.title}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{v.validity}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-2 rounded-lg border border-dashed border-border px-3 py-4 text-xs text-muted-foreground">
                {t('crm.p.noVouchers')}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ---- Orders · Invoices · Reservations --------------------------- */}
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border">
          <TabsList className="bg-transparent p-0 h-auto">
            {[
              ['orders', t('crm.p.tabOrders'), recent.orders.length],
              ['invoices', t('crm.p.tabInvoices'), recent.invoices.length],
              ['reservations', t('crm.p.tabReservations'), recent.reservations.length],
            ].map(([key, label, n]) => (
              <TabsTrigger key={key} value={key}
                className="rounded-none border-b-2 border-transparent px-4 py-2.5 text-sm data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                {label} <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{n}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          <span className="text-sm text-muted-foreground">{t('crm.p.showing')}</span>
        </div>

        <TabsContent value="orders">
          <RowsTable
            empty={t('crm.p.noRows')}
            head={[t('crm.p.colOrder'), t('crm.p.colDate'), t('crm.p.colLocation'), t('crm.p.colTable'), t('crm.p.colStatus'), t('crm.p.colAmount')]}
            rows={recent.orders.map((o) => [o.order_number, dateTime(o.created_at), o.location || '—', o.table || '—', o.status, money(o.total_amount)])}
          />
        </TabsContent>
        <TabsContent value="invoices">
          <RowsTable
            empty={t('crm.p.noRows')}
            head={[t('crm.p.colInvoice'), t('crm.p.colDate'), t('crm.p.colLocation'), t('crm.p.colAmount')]}
            rows={recent.invoices.map((o) => [o.order_number, dateTime(o.created_at), o.location || '—', money(o.total_amount)])}
          />
        </TabsContent>
        <TabsContent value="reservations">
          <RowsTable
            empty={t('crm.p.noRows')}
            head={[t('crm.p.colBooking'), t('crm.p.colDate'), t('crm.p.colPax'), t('crm.p.colTable'), t('crm.p.colStatus'), t('crm.p.colPayment')]}
            rows={recent.reservations.map((x) => [
              x.reservation_code || `#${x.id}`, dateTime(x.reserved_start_at), x.pax ?? '—',
              x.table_code || (x.buffet_name ? x.buffet_name : '—'), x.status,
              x.payment_amount != null ? `${money(x.payment_amount)} · ${x.payment_status}` : '—',
            ])}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* One plain table for the three tabs: the columns differ, the reading does not. */
function RowsTable({ head, rows, empty }) {
  if (!rows.length) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  const last = head.length - 1;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="text-xs text-muted-foreground">
          <tr>
            {head.map((h, i) => (
              <th key={h} className={`py-2 pr-4 font-medium ${i === last ? 'text-right' : 'text-left'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((cells, ri) => (
            <tr key={ri}>
              {cells.map((cell, ci) => (
                <td key={ci} className={`py-2 pr-4 ${ci === 0 ? 'font-medium text-foreground' : 'text-foreground'} ${ci === last ? 'text-right' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
